import React from 'react';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { useNavigate } from 'react-router-dom';
import './OurTeam.css';

const directors = [
    {
        name: 'Manav Bhasin',
        title: 'Co-Founder & Director',
        age: 22,
        bio: 'Visionary entrepreneur driving innovation in composite reinforcement technology. Leading strategic growth and establishing BLITZ as India\'s premier GFRP manufacturer.',
        expertise: ['Business Strategy', 'Operations', 'Market Expansion', 'Client Relations'],
        image: '/manav.webp',
        initial: 'M'
    },
    {
        name: 'Ansh Chopra',
        title: 'Co-Founder & Director',
        age: 26,
        bio: 'Technical visionary championing sustainable construction solutions. Spearheading product development and engineering excellence at BLITZ Fiber.',
        expertise: ['Product Development', 'Technical Strategy', 'Quality Control', 'Innovation'],
        image: '/ansh.webp',
        initial: 'A'
    }
];

const teamStats = [
    {
        icon: '👷',
        number: '25+',
        label: 'Skilled Operators'
    },
    {
        icon: '👨‍🔧',
        number: '12+',
        label: 'Engineers'
    },
    {
        icon: '📊',
        number: '8+',
        label: 'Quality Specialists'
    },
    {
        icon: '🚚',
        number: '15+',
        label: 'Logistics Team'
    }
];

const departments = [
    {
        icon: '⚙️',
        name: 'Manufacturing',
        description: 'State-of-the-art production facility with skilled operators ensuring consistent quality and precision in every GFRP rebar manufactured.'
    },
    {
        icon: '🔬',
        name: 'Quality Assurance',
        description: 'Dedicated team conducting rigorous testing and quality control at every production stage to maintain international standards.'
    },
    {
        icon: '🛠️',
        name: 'Engineering',
        description: 'Expert engineers providing technical support, custom solutions, and continuous product innovation for complex projects.'
    },
    {
        icon: '📦',
        name: 'Supply Chain',
        description: 'Efficient logistics team ensuring timely delivery and seamless coordination from factory to construction site nationwide.'
    },
    {
        icon: '💼',
        name: 'Sales & Support',
        description: 'Customer-focused professionals delivering exceptional service, technical consultation, and long-term partnership support.'
    },
    {
        icon: '🔧',
        name: 'R&D',
        description: 'Innovation-driven team developing next-generation composite solutions and advancing GFRP reinforcement technology.'
    }
];

const cultureValues = [
    {
        icon: '🚀',
        name: 'Innovation First',
        description: 'Constantly pushing boundaries in composite technology and sustainable construction solutions.'
    },
    {
        icon: '🤝',
        name: 'Teamwork',
        description: 'Collaborative environment where every team member contributes to our collective success.'
    },
    {
        icon: '✨',
        name: 'Excellence',
        description: 'Committed to delivering superior quality and exceeding expectations in every project.'
    }
];

function OurTeam() {
    const navigate = useNavigate();

    return (
        <div className="our-team">
            <Navbar />

            {/* Hero Section */}
            <div className="team-hero-section">
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: 'url(/construction.webp)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.06,
                        zIndex: 1
                    }}
                />
                <div className="team-hero-container">
                    <div className="team-hero-badge">
                        Meet Our Team
                    </div>
                    <h1 className="team-hero-title">Building the Future Together</h1>
                    <p className="team-hero-subtitle">
                        A young, dynamic team of visionaries, engineers, and craftsmen united by a mission to revolutionize
                        construction with sustainable, high-performance GFRP solutions.
                    </p>
                </div>
            </div>

            {/* Leadership Section */}
            <div className="team-leadership-section">
                <div className="leadership-container">
                    <div className="leadership-header">
                        <h2 className="leadership-title">Leadership Team</h2>
                        <p className="leadership-subtitle">
                            Young entrepreneurs driving innovation and excellence in composite reinforcement
                        </p>
                    </div>

                    <div className="leadership-grid">
                        {directors.map((director, index) => (
                            <div key={index} className="director-card">
                                <div className="director-image-wrapper">
                                    <div className="director-image-circle">
                                        {director.image ? (
                                            <img
                                                src={director.image}
                                                alt={director.name}
                                                className="director-image"
                                            />
                                        ) : (
                                            director.initial
                                        )}
                                    </div>
                                    <div className="director-badge">Director</div>
                                </div>
                                <div className="director-info">
                                    <h3 className="director-name">{director.name}</h3>
                                    <p className="director-title">{director.title}</p>
                                    {director.age && (
                                        <p className="director-age">Age: {director.age}</p>
                                    )}
                                    <p className="director-bio">{director.bio}</p>
                                    <div className="director-expertise">
                                        {director.expertise.map((skill, idx) => (
                                            <span key={idx} className="expertise-tag">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Team Statistics Section */}
            <div className="team-stats-section">
                <div className="team-stats-container">
                    <div className="team-stats-header">
                        <h2 className="team-stats-title">Our Team by Numbers</h2>
                        <p className="team-stats-subtitle">
                            Dedicated professionals working together to deliver excellence
                        </p>
                    </div>

                    <div className="team-stats-grid">
                        {teamStats.map((stat, index) => (
                            <div key={index} className="team-stat-card">
                                <div className="team-stat-icon">{stat.icon}</div>
                                <div className="team-stat-number">{stat.number}</div>
                                <div className="team-stat-label">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Manufacturing Plant Section */}
            <div className="team-plant-section">
                <div className="team-plant-container">
                    <div className="team-plant-image-wrapper">
                        <img
                            src="/plant.webp"
                            alt="BLITZ Manufacturing Plant"
                            className="team-plant-image"
                        />
                    </div>
                    <div className="team-plant-content">
                        <p className="team-plant-tagline">Our Manufacturing Facility</p>
                        <h2 className="team-plant-title">State-of-the-Art Production Plant</h2>
                        <p className="team-plant-description">
                            Our advanced manufacturing facility in Meerut represents the pinnacle of GFRP production
                            technology in India. Spanning 3 acres, this state-of-the-art plant houses cutting-edge
                            pultrusion equipment and quality control systems, enabling us to produce high-performance
                            composite rebars with consistent quality and precision.
                        </p>
                        <div className="team-plant-details">
                            <div className="team-plant-detail-item">
                                <div className="team-plant-detail-icon">📍</div>
                                <div className="team-plant-detail-content">
                                    <h4 className="team-plant-detail-label">Location</h4>
                                    <p className="team-plant-detail-value">Bulandshahr, Uttar Pradesh</p>
                                </div>
                            </div>
                            <div className="team-plant-detail-item">
                                <div className="team-plant-detail-icon">🏭</div>
                                <div className="team-plant-detail-content">
                                    <h4 className="team-plant-detail-label">Facility Area</h4>
                                    <p className="team-plant-detail-value">3 Acres</p>
                                </div>
                            </div>
                            <div className="team-plant-detail-item">
                                <div className="team-plant-detail-icon">⚙️</div>
                                <div className="team-plant-detail-content">
                                    <h4 className="team-plant-detail-label">Production Capacity</h4>
                                    <p className="team-plant-detail-value">High-Volume Continuous Production</p>
                                </div>
                            </div>
                            <div className="team-plant-detail-item">
                                <div className="team-plant-detail-icon">🔬</div>
                                <div className="team-plant-detail-content">
                                    <h4 className="team-plant-detail-label">Quality Standards</h4>
                                    <p className="team-plant-detail-value">ISO Certified, Rigorous QC</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Departments Section */}
            <div className="team-departments-section">
                <div className="departments-header">
                    <h2 className="departments-title">Our Departments</h2>
                    <p className="departments-subtitle">
                        Specialized teams working in harmony to deliver world-class GFRP solutions
                    </p>
                </div>

                <div className="departments-grid">
                    {departments.map((dept, index) => (
                        <div key={index} className="department-card">
                            <div className="department-icon">{dept.icon}</div>
                            <h3 className="department-name">{dept.name}</h3>
                            <p className="department-description">{dept.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Culture Section */}
            <div className="team-culture-section">
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: 'url(/construction.webp)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.05,
                        zIndex: 1
                    }}
                />
                <div className="team-culture-container">
                    <div className="team-culture-header">
                        <h2 className="team-culture-title">Our Values & Culture</h2>
                        <p className="team-culture-subtitle">
                            The principles that guide everything we do
                        </p>
                    </div>

                    <div className="culture-values-grid">
                        {cultureValues.map((value, index) => (
                            <div key={index} className="culture-value-card">
                                <div className="culture-value-icon">{value.icon}</div>
                                <h3 className="culture-value-name">{value.name}</h3>
                                <p className="culture-value-description">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="team-cta-section">
                <div className="team-cta-container">
                    <h2 className="team-cta-title">Want to Join Our Team?</h2>
                    <p className="team-cta-description">
                        We're always looking for talented individuals who share our passion for innovation
                        and sustainable construction. Be part of something extraordinary.
                    </p>
                    <button
                        className="team-cta-button"
                        onClick={() => navigate('/contact')}
                    >
                        Get In Touch
                    </button>
                </div>
            </div>

            <Footer />
        </div>
    );
}

export default OurTeam;

